import { api } from '@/shared/api/client'
import { qs, type QueryValue } from '@/shared/lib/query'
import type {
  ExternalPollDto, PageResponse, PollOfPollsDto, SurveyAssignmentDto, SurveyBackCheckDto,
  SurveyCodeDto, SurveyCodeFrameDto, SurveyCrewReportDto, SurveyCrosstabDto, SurveyDispositionDto,
  SurveyEstimateSetDto, SurveyExportJobDto, SurveyFieldProgressDto, SurveyInstrumentDto,
  SurveyPcaDto, SurveyQualitySummaryDto, SurveyQuotaStatusDto, SurveyResponseDto,
  SurveyResponseSummaryDto, SurveySegmentationDto, SurveyStratumDto, SurveyVersionResponseDto,
  SurveyWeightRunDto, SurveyCrosstabSummaryDto, SurveyBundleDto, SurveyContactAttemptDto,
  SurveyGroupDto, SurveyDesignReadinessDto, SurveyPopulationFrameDto, SurveyQuotaDimensionDto,
  SurveyMediaListDto, SurveyMediaPlaybackDto, SurveyReservedMediaDto, SurveyMediaDto,
  SurveyResponseDetailDto, SurveyBatchResultDto, SurveyInterviewerIntegrityDto,
  SurveyVocabularyDto, SurveySkipLogicVocabularyDto, SurveyFlagDto,
  SurveyGeoDto, TrackerTrendDto,
  SurveyLinkDto, SurveyLinkSuggestionDto, SurveyLinkableTypeDto, SurveySampleSizeDto,
  SurveyScheduleDto, RespondentLookupDto, SurveyRevealedIdentifiersDto,
} from '@/shared/api/types'

/**
 * The Polling Studio.
 *
 * The web feature is `polling`; the backend package is `survey`, and every path
 * below says `surveys`. That mismatch is deliberate and load-bearing in one
 * direction only: the product name is what people call it, and the API path is
 * what the server serves. Do not "tidy" one into the other.
 *
 * ## Four rules that govern almost every call here
 *
 * **1. A suppressed estimate is a ROW, not an absence.** `/estimates` returns
 * cells with `estimate: null`, `suppressed: true` and a `suppressedSentence`
 * composed server-side. A screen renders the sentence. There is no
 * `?? 0` anywhere in this feature, and a chart that plots zero for a suppressed
 * cell is stating that nobody supports that candidate — which is a claim nobody
 * made. The same rule applies to `SurveyDispositionDto`, whose rates are null
 * rather than zero when their denominator is empty.
 *
 * **2. Nothing here computes a percentage.** Every share, margin, chi-square and
 * design effect comes from the server with its base and its assumptions
 * attached. PollSphere's client recomputed several of them and disagreed with
 * its own tables; there is no arithmetic on raw responses in this feature.
 *
 * **3. Identifiers are masked unless the caller holds `survey:read-raw`,** and
 * an export that includes them needs `survey:export-pii` AND a written
 * justification the server stores. A screen offers the control and lets the
 * server refuse — a hidden control cannot be told apart from a broken one.
 *
 * **4. The instrument is versioned and a published version is immutable.** Every
 * editing call goes to `/survey-versions/{id}`, and a 409 with
 * `survey/version-published` is the server telling the UI to offer "create
 * version N+1" rather than an error to apologise for.
 */

// ── polls ───────────────────────────────────────────────────────────────────

export interface PollFilter {
  statusCode?: string
  categoryCode?: string
  purposeCode?: string
  scopeUnitId?: string
  groupId?: string
  search?: string
}

/**
 * List polls.
 *
 * The wire names are `status`, `category`, `purpose` — NOT the `*Code` names the
 * DTO uses. `SurveyFilter` is built positionally in the controller from three
 * `@RequestParam`s, so a `statusCode=LIVE` query binds nothing, Spring ignores
 * the unknown parameter and the server cheerfully returns the UNFILTERED list.
 * That failure is silent in both directions: no 400, no empty result, just the
 * wrong rows under the right chip. The mapping is written out here so the next
 * person can see it rather than infer it.
 */
export function listPolls(filter: PollFilter, page = 0, size = 25) {
  const query: Record<string, QueryValue> = {
    status: filter.statusCode,
    category: filter.categoryCode,
    purpose: filter.purposeCode,
    scopeUnitId: filter.scopeUnitId,
    groupId: filter.groupId,
    search: filter.search,
    page,
    size,
  }
  return api.get<PageResponse<SurveyResponseDto>>(`/surveys${qs(query)}`)
}

export function getPoll(id: string) {
  return api.get<SurveyResponseDto>(`/surveys/${id}`)
}

export function createPoll(body: Record<string, unknown>) {
  return api.post<SurveyResponseDto>('/surveys', body)
}

export function updatePoll(id: string, body: Record<string, unknown>) {
  return api.put<SurveyResponseDto>(`/surveys/${id}`, body)
}

/**
 * Move a poll along its state machine.
 *
 * `PAUSED` and `CLOSED` require a reason — the server refuses without one, and
 * the dialog asks for it rather than discovering the refusal.
 */
export function transitionPoll(id: string, statusCode: string, reason?: string) {
  return api.post<SurveyResponseDto>(`/surveys/${id}/status`, { statusCode, reason })
}

// ── instrument ──────────────────────────────────────────────────────────────

export function getInstrument(versionId: string) {
  return api.get<SurveyInstrumentDto>(`/survey-versions/${versionId}`)
}

export function listVersions(surveyId: string) {
  return api.get<SurveyVersionResponseDto[]>(`/surveys/${surveyId}/versions`)
}

/**
 * Copy-on-write. The published version keeps every answer already pointing at it.
 *
 * `changeNote` is a QUERY PARAMETER. There is no `@RequestBody` on that handler,
 * so a JSON body is discarded silently and every version ships with a null note —
 * which is only discovered months later when somebody asks what changed in v3.
 */
export function createNextVersion(surveyId: string, changeNote: string) {
  return api.post<SurveyVersionResponseDto>(
    `/surveys/${surveyId}/versions${qs({ changeNote })}`, {})
}

export function publishVersion(versionId: string) {
  return api.post<SurveyVersionResponseDto>(`/survey-versions/${versionId}/publish`, {})
}

// ── design ──────────────────────────────────────────────────────────────────

export function listStrata(surveyId: string) {
  return api.get<SurveyStratumDto[]>(`/surveys/${surveyId}/design/strata`)
}

export function getQuotaStatus(surveyId: string) {
  return api.get<SurveyQuotaStatusDto>(`/surveys/${surveyId}/design/quota-status`)
}

// ── fieldwork ───────────────────────────────────────────────────────────────

export function getFieldProgress(surveyId: string) {
  return api.get<SurveyFieldProgressDto>(`/surveys/${surveyId}/field-progress`)
}

export function listAssignments(surveyId: string) {
  return api.get<SurveyAssignmentDto[]>(`/surveys/${surveyId}/assignments`)
}

export function assign(surveyId: string, body: Record<string, unknown>) {
  return api.post<SurveyAssignmentDto>(`/surveys/${surveyId}/assignments`, body)
}

/**
 * A whole crew, posted station by station, in one call.
 *
 * It REPORTS rather than refusing: the common failure on a roster of forty is
 * that three of them already hold an open assignment, and refusing all forty
 * for that makes the call useless at the scale it exists for. Render both lists
 * — `refused` carries a sentence per person.
 */
export function postCrew(surveyId: string, body: {
  postings: { mobilizerId: string; unitId: string }[]
  targetCountEach: number | null
  dueAt: string | null
}) {
  return api.post<SurveyCrewReportDto>(`/surveys/${surveyId}/assignments/crew`, body)
}

/** A revocation needs a reason. The assignment is kept — its attempts are the record. */
export function revokeAssignment(surveyId: string, assignmentId: string, reason: string) {
  return api.post<SurveyAssignmentDto>(
    `/surveys/${surveyId}/assignments/${assignmentId}/revoke`, { reason })
}

export function getDisposition(surveyId: string) {
  return api.get<SurveyDispositionDto>(`/surveys/${surveyId}/disposition`)
}

// ── responses ───────────────────────────────────────────────────────────────

/**
 * Every interview on a poll (backend prompt 65).
 *
 * `survey:read-responses`, split out from `survey:read`: reading a poll's design
 * and paging through four hundred people's individual answers are different
 * disclosures. Identifiers are masked for every caller — the plaintext is a
 * separate route and a separate authority.
 */
export function listResponses(surveyId: string, filter: {
  statusCode?: string
  dispositionCode?: string
  mobilizerId?: string
} = {}, page = 0, size = 25) {
  return api.get<PageResponse<SurveyResponseSummaryDto>>(
    `/surveys/${surveyId}/responses${qs({ ...filter, page, size } as Record<string, QueryValue>)}`,
  )
}

/**
 * The identifiers behind the masks, for ONE interview.
 *
 * `survey:read-raw`, one call per interview, one audit row per call. POST rather
 * than GET so a reveal does not land in browser history or a cache, and never a
 * bulk flag on the list — the point of the separate authority is that this stays
 * rare and deliberate.
 */
export function revealIdentifiers(responseId: string) {
  return api.post<SurveyRevealedIdentifiersDto>(`/survey-responses/${responseId}/reveal`, {})
}

// ── quality ─────────────────────────────────────────────────────────────────

export function getQualityQueue(surveyId: string) {
  return api.get<SurveyQualitySummaryDto>(`/surveys/${surveyId}/quality`)
}

export function resolveFlag(flagId: string, resolutionCode: string, note?: string) {
  return api.put<unknown>(`/survey-flags/${flagId}/resolution`, { resolutionCode, note })
}

export function rejectResponse(responseId: string, reason: string) {
  return api.post<void>(`/survey-responses/${responseId}/reject`, { reason })
}

export function acceptResponse(responseId: string, note?: string) {
  return api.post<void>(`/survey-responses/${responseId}/accept`,
    { resolutionCode: 'DISMISSED', note })
}

export function listBackChecks(surveyId: string) {
  return api.get<SurveyBackCheckDto[]>(`/surveys/${surveyId}/back-checks`)
}

// ── analysis ────────────────────────────────────────────────────────────────

export function getEstimates(surveyId: string) {
  return api.get<SurveyEstimateSetDto>(`/surveys/${surveyId}/estimates`)
}

export function listWeightRuns(surveyId: string) {
  return api.get<SurveyWeightRunDto[]>(`/surveys/${surveyId}/weight-runs`)
}

/** 202: the run is QUEUED and a poller picks it up. The numbers are not ready yet. */
export function startWeightRun(surveyId: string) {
  return api.post<SurveyWeightRunDto>(`/surveys/${surveyId}/weight-runs`, {})
}

export function crosstab(surveyId: string, rowQuestionId: string, colQuestionId: string) {
  return api.post<SurveyCrosstabDto>(
    `/surveys/${surveyId}/crosstabs${qs({ rowQuestionId, colQuestionId })}`, {})
}

/**
 * Previously computed tables — SUMMARIES, without cells.
 *
 * The list shape is not the detail shape: no `cells`, no `surveyId`, no question
 * TEXT. Typing this as `SurveyCrosstabDto[]` compiles and then hands every
 * consumer an `undefined` where it expects an array.
 */
export function listCrosstabs(surveyId: string) {
  return api.get<SurveyCrosstabSummaryDto[]>(`/surveys/${surveyId}/crosstabs`)
}

export function segment(surveyId: string, questionIds: string[], k: number) {
  return api.post<SurveySegmentationDto>(`/surveys/${surveyId}/segments`, { questionIds, k })
}

export function components(surveyId: string, questionIds: string[]) {
  return api.post<SurveyPcaDto>(`/surveys/${surveyId}/components`, { questionIds })
}

// ── output ──────────────────────────────────────────────────────────────────

export function listExports(surveyId: string) {
  return api.get<SurveyExportJobDto[]>(`/surveys/${surveyId}/exports`)
}

/**
 * Generate a dataset.
 *
 * Synchronous: 201 with a download URL, because the file exists by the time this
 * returns. `SPSS_SAV` and `STATA_DTA` are declared by the server and REFUSED with
 * a sentence naming the alternative — the picker offers them and lets that
 * sentence be the answer.
 */
export function requestExport(surveyId: string, body: {
  formatCode: string
  piiModeCode?: string
  versionId?: string | null
  includeFlagged?: boolean
  justification?: string
}) {
  return api.post<SurveyExportJobDto>(`/surveys/${surveyId}/exports`, body)
}

/** A fresh presigned URL. The first expires and the file outlives it. */
export function exportStatus(jobId: string) {
  return api.get<SurveyExportJobDto>(`/survey-exports/${jobId}`)
}

export function getCodeFrame(surveyId: string, questionId: string) {
  return api.get<SurveyCodeFrameDto>(`/surveys/${surveyId}/codes${qs({ questionId })}`)
}

export function addCode(surveyId: string, body: Record<string, unknown>) {
  return api.post<SurveyCodeDto>(`/surveys/${surveyId}/codes`, body)
}

export function applyCodes(surveyId: string, answerId: string, codeIds: string[], note?: string) {
  return api.post<SurveyCodeFrameDto>(`/surveys/${surveyId}/codes/apply`,
    { answerId, codeIds, note })
}

export function listExternalPolls(unitId?: string) {
  return api.get<ExternalPollDto[]>(`/external-polls${qs({ unitId })}`)
}

export function ingestExternalPoll(body: Record<string, unknown>) {
  return api.post<ExternalPollDto>('/external-polls', body)
}

export function getPollOfPolls(unitId?: string, seatType?: string) {
  return api.get<PollOfPollsDto>(`/poll-of-polls${qs({ unitId, seatType })}`)
}

// ── vocabulary ──────────────────────────────────────────────────────────────

/**
 * The code lists, from the server.
 *
 * `vocab.tsx` holds LABELS — what a code is called in English. It must not hold
 * the code list itself: PollSphere's filter dropdowns were hand-written copies of
 * its Prisma enums and drifted, so `STATION`, `TRACKING`, `OPPOSITION_RESEARCH`,
 * `YOUTH`, `WOMEN` and `RURAL` were all offered by the UI and matched nothing on
 * the server. Selecting one returned an empty list with no error — the worst
 * possible failure, because it looks like an answer.
 *
 * So every picker is populated from here, and `vocab.tsx` only decides what to
 * call what comes back. A code this build has never seen renders as its raw code
 * rather than being dropped.
 */
export function getVocabulary() {
  return api.get<SurveyVocabularyDto>('/surveys/vocabulary')
}

export function getSkipLogicVocabulary() {
  return api.get<SurveySkipLogicVocabularyDto>('/surveys/vocabulary/skip-logic')
}

/** Gender and age bands — the REGISTER's vocabulary, not a second copy of it. */
export function getRegisterVocabulary() {
  return api.get<Record<string, string[]>>('/surveys/vocabulary/register')
}

// ── instrument editing ──────────────────────────────────────────────────────

/**
 * Every write below returns the WHOLE instrument, not the thing that changed.
 *
 * Adding a question renumbers positions, a skip rule can be invalidated by a
 * deletion, and an option code can collide. Returning the whole document means
 * the client never reconstructs a state the server did not confirm — and a 409
 * `survey/version-published` means the caller should offer "create version N+1"
 * rather than apologise.
 */
export function addQuestion(versionId: string, body: Record<string, unknown>) {
  return api.post<SurveyInstrumentDto>(`/survey-versions/${versionId}/questions`, body)
}

export function updateQuestion(questionId: string, body: Record<string, unknown>) {
  return api.put<SurveyInstrumentDto>(`/survey-versions/questions/${questionId}`, body)
}

export function deleteQuestion(questionId: string) {
  return api.del<void>(`/survey-versions/questions/${questionId}`)
}

/** A bare JSON array of question ids, in the order they should appear. */
export function reorderQuestions(versionId: string, questionIds: string[]) {
  return api.put<SurveyInstrumentDto>(
    `/survey-versions/${versionId}/question-order`, questionIds)
}

export function addBlock(versionId: string, body: Record<string, unknown>) {
  return api.post<SurveyInstrumentDto>(`/survey-versions/${versionId}/blocks`, body)
}

export function addSkipRule(versionId: string, body: Record<string, unknown>) {
  return api.post<SurveyInstrumentDto>(`/survey-versions/${versionId}/skip-rules`, body)
}

export function deleteSkipRule(ruleId: string) {
  return api.del<void>(`/survey-versions/skip-rules/${ruleId}`)
}

export function setQuestionTranslation(questionId: string, body: {
  languageCode: string; text: string; helpText?: string
}) {
  return api.put<void>(`/survey-versions/questions/${questionId}/translations`, body)
}

export function setOptionTranslation(optionId: string, body: {
  languageCode: string; text: string
}) {
  return api.put<void>(`/survey-versions/options/${optionId}/translations`, body)
}

/**
 * The consent script.
 *
 * Not optional decoration: `→ LIVE` is refused unless a script exists in every
 * declared language when `consentRequired`, and audio recording additionally
 * needs its disclosure clause. Nothing else in the app can create one.
 */
export function setConsentScript(surveyId: string, body: {
  languageCode: string; body: string; audioDisclosure?: string
}) {
  return api.put<void>(`/surveys/${surveyId}/consent-script`, body)
}

export function deletePoll(id: string, reason?: string) {
  return api.del<void>(`/surveys/${id}${qs({ reason })}`)
}

// ── design, the write half ──────────────────────────────────────────────────

export function createStratum(surveyId: string, body: Record<string, unknown>) {
  return api.post<SurveyStratumDto>(`/surveys/${surveyId}/design/strata`, body)
}

export function updateStratum(surveyId: string, stratumId: string, body: Record<string, unknown>) {
  return api.put<SurveyStratumDto>(`/surveys/${surveyId}/design/strata/${stratumId}`, body)
}

export function deleteStratum(surveyId: string, stratumId: string) {
  return api.del<void>(`/surveys/${surveyId}/design/strata/${stratumId}`)
}

/** Dimension and its cells in one write — a dimension with no cells enforces nothing. */
export function saveQuotaDimension(surveyId: string, body: Record<string, unknown>) {
  return api.post<SurveyQuotaDimensionDto>(
    `/surveys/${surveyId}/design/quota-dimensions`, body)
}

export function getPopulationFrame(surveyId: string) {
  return api.get<SurveyPopulationFrameDto[]>(`/surveys/${surveyId}/design/population-frame`)
}

/**
 * One row of the population this poll will be raked to.
 *
 * `sourceNote` is required by the server and that is the point: "weighted to the
 * population" is a claim that has to name the population — KNBS 2019, an IEBC
 * register extract, a previous wave. A frame with no provenance is a number
 * somebody made up.
 */
export function setPopulationFrameRow(surveyId: string, body: {
  dimensionCode: string; valueCode: string; populationCount: number
  sourceNote: string; asOf?: string | null
}) {
  return api.put<SurveyPopulationFrameDto>(
    `/surveys/${surveyId}/design/population-frame`, body)
}

/** Can this poll be weighted at all — asked while there is still time to fix it. */
export function getDesignReadiness(surveyId: string) {
  return api.get<SurveyDesignReadinessDto>(`/surveys/${surveyId}/design/readiness`)
}

// ── trackers and waves ──────────────────────────────────────────────────────

export function listGroups() {
  return api.get<SurveyGroupDto[]>('/survey-groups')
}

export function getGroup(id: string) {
  return api.get<SurveyGroupDto>(`/survey-groups/${id}`)
}

export function createGroup(body: { code: string; name: string; description?: string }) {
  return api.post<SurveyGroupDto>('/survey-groups', body)
}

export function updateGroup(id: string, body: Record<string, unknown>) {
  return api.put<SurveyGroupDto>(`/survey-groups/${id}`, body)
}

/** `waveNo` is assigned server-side as max+1 inside the transaction. Do not send one. */
export function addWave(groupId: string, body: {
  label?: string; opensAt?: string | null; closesAt?: string | null
}) {
  return api.post<SurveyGroupDto>(`/survey-groups/${groupId}/waves`, body)
}

// ── fieldwork, the interviewer's half ───────────────────────────────────────

/** Resolved from the token — an interviewer never passes their own id. */
export function myAssignments() {
  return api.get<SurveyAssignmentDto[]>('/surveys/my-assignments')
}

export function listAttempts(surveyId: string, assignmentId: string) {
  return api.get<SurveyContactAttemptDto[]>(
    `/surveys/${surveyId}/assignments/${assignmentId}/attempts`)
}

/**
 * Record a contact attempt.
 *
 * This is the table AAPOR RR1/RR3, COOP1, REF1 and CON1 are computed from.
 * Without it every one of those rates is null and the Fieldwork screen can only
 * show "collected / target" — which is what PollSphere shows, because it has no
 * concept of an attempted-but-not-completed contact at all.
 */
export function recordAttempt(surveyId: string, assignmentId: string, body: {
  outcomeCode: string; attemptedAt?: string; unitId?: string | null; note?: string
}) {
  return api.post<SurveyContactAttemptDto>(
    `/surveys/${surveyId}/assignments/${assignmentId}/attempts`, body)
}

/**
 * Everything needed to conduct interviews for one poll, in one cacheable read.
 *
 * Instrument, options, translations, skip rules, consent script and live quota
 * state, with an ETag. One request, not seven, because the field device may be
 * on a bus.
 */
export function getBundle(surveyId: string) {
  return api.get<SurveyBundleDto>(`/surveys/${surveyId}/bundle`)
}

// ── capture ─────────────────────────────────────────────────────────────────

/**
 * Submit one interview.
 *
 * `idempotencyKey` is not optional and not a nicety. A field device on a flaky
 * connection retries; without the key the retry is a second respondent who never
 * existed, and nothing downstream can tell the difference. The server returns the
 * ORIGINAL response for a repeated key rather than a 409, so a retry is safe.
 */
export function submitResponse(surveyId: string, body: Record<string, unknown>) {
  return api.post<SurveyResponseDetailDto>(`/surveys/${surveyId}/responses`, body)
}

/** The offline queue, drained in one call. Per-item results; a bad row does not lose the batch. */
export function submitBatch(surveyId: string, responses: Record<string, unknown>[]) {
  return api.post<SurveyBatchResultDto>(`/surveys/${surveyId}/responses/batch`, { responses })
}

export function getResponse(responseId: string) {
  return api.get<SurveyResponseDetailDto>(`/survey-responses/${responseId}`)
}

export function listFlags(responseId: string) {
  return api.get<SurveyFlagDto[]>(`/survey-responses/${responseId}/flags`)
}

// ── media ───────────────────────────────────────────────────────────────────

/**
 * Reserve an object, then PUT the bytes straight at storage.
 *
 * Audio never transits the application. The server checks the purpose policy —
 * content type and size — BEFORE minting the URL, so an interviewer learns their
 * 200MB clip is refused before they spend the upload, not after.
 */
export function reserveMedia(responseId: string, body: {
  kindCode: string; contentType: string; sizeBytes: number
  questionId?: string | null; durationSeconds?: number; capturedAt?: string
}) {
  return api.post<SurveyReservedMediaDto>(`/survey-responses/${responseId}/media`, body)
}

export function completeMedia(responseId: string, mediaId: string, body: {
  checksumSha256?: string; etag?: string
}) {
  return api.put<SurveyMediaDto>(
    `/survey-responses/${responseId}/media/${mediaId}/complete`, body)
}

export function listMedia(responseId: string) {
  return api.get<SurveyMediaListDto>(`/survey-responses/${responseId}/media`)
}

/**
 * A short-lived presigned GET, and an audit row naming the listener.
 *
 * `survey:listen` is deliberately separate from `survey:read`: reading what
 * somebody said and hearing them say it are different disclosures.
 */
export function getPlayback(mediaId: string) {
  return api.get<SurveyMediaPlaybackDto>(`/survey-media/${mediaId}/playback`)
}

/**
 * Consent withdrawn.
 *
 * Purges the media and the identifiers, marks the interview WITHDRAWN and drops
 * it from every estimate. Irreversible, which is why the reason is mandatory.
 */
export function withdrawResponse(responseId: string, reason: string) {
  return api.post<void>(`/survey-responses/${responseId}/withdraw`, { reason })
}

// ── back-checks ─────────────────────────────────────────────────────────────

export function assignBackCheck(backCheckId: string, assignedTo: string | null) {
  return api.put<SurveyBackCheckDto>(
    `/survey-back-checks/${backCheckId}/assignee`, { assignedTo })
}

/**
 * The verifier's finding.
 *
 * `FABRICATED` is the one that matters: it flags every other interview by that
 * interviewer on that poll for review. Detection without a verification path is
 * just a list, which is what PollSphere's copy promises and does not have.
 */
export function recordBackCheckOutcome(backCheckId: string, body: {
  outcomeCode: string; mismatchCount: number; note?: string
}) {
  return api.post<SurveyBackCheckDto>(`/survey-back-checks/${backCheckId}/outcome`, body)
}

export function getInterviewerIntegrity(mobilizerId: string) {
  return api.get<SurveyInterviewerIntegrityDto>(
    `/survey-interviewers/${mobilizerId}/integrity`)
}

export function getExternalPoll(id: string) {
  return api.get<ExternalPollDto>(`/external-polls/${id}`)
}

// ── the two reads backend prompt 60 added ───────────────────────────────────

/**
 * Where the fieldwork happened, aggregated to units.
 *
 * **This is not a list of respondent coordinates and must never be treated as
 * one.** `centroidLat`/`centroidLng` are the geography UNIT's centroid; a
 * respondent's own fix never leaves the database. A cell holding fewer than
 * `minCellBase` interviews is not returned at all — one interview in a ward,
 * plotted with a leading candidate, IS the respondent, and the floor is the
 * disclosure control rather than a display nicety. `suppressedCells` counts what
 * was withheld so the screen can say so.
 *
 * `survey:read`, not `survey:analyse`: the only figure here that is an estimate
 * is `leadingShare`, and it is absent whenever the weight run that would justify
 * it is.
 */
export function getResponseGeo(surveyId: string, level: string, questionId?: string) {
  return api.get<SurveyGeoDto>(`/surveys/${surveyId}/geo${qs({ level, questionId })}`)
}

/**
 * One question, wave on wave.
 *
 * Every point comes from `survey_estimate` — nothing is recomputed and nothing
 * is pooled across waves. The field that matters most is `deltaPp`, which is
 * **null** whenever the two waves' instruments are not comparable, with
 * `comparabilityNote` saying what changed. A tracker whose question was reworded
 * between waves has a change of wording and a change of opinion tangled
 * together, and a delta that pretends otherwise is the PollSphere `momentum`
 * defect wearing better clothes.
 */
export function getTrackerTrend(groupId: string, questionId: string, optionIds?: string[]) {
  return api.get<TrackerTrendDto>(
    `/survey-groups/${groupId}/trend${qs({ questionId, optionId: optionIds })}`)
}

/** Party, coalition and candidate statuses plus the coalition role. */
export function getPoliticsVocabulary() {
  return api.get<Record<string, string[]>>('/politics/vocabulary')
}

// ── the configurator's setup steps (backend prompt 61) ──────────────────────

/**
 * What a poll is ABOUT — candidates, programmes, parties, units.
 *
 * A survey-level link is a DIFFERENT statement from a question-level one, and
 * both exist: a ballot option links to the candidate it names, and the poll
 * links to the candidates it was commissioned to measure. Deriving the second
 * from the first would make a poll stop being about somebody the moment its
 * ballot question was reworded.
 */
export function listSurveyLinks(surveyId: string) {
  return api.get<SurveyLinkDto[]>(`/surveys/${surveyId}/links`)
}

export function addSurveyLink(surveyId: string, body: {
  typeCode: string
  ref: string
  note?: string | null
}) {
  return api.post<SurveyLinkDto>(`/surveys/${surveyId}/links`, body)
}

export function removeSurveyLink(surveyId: string, linkId: string) {
  return api.del<void>(`/surveys/${surveyId}/links/${linkId}`)
}

/** Which types this deployment can resolve. Never a hardcoded list on the client. */
export function listLinkableTypes() {
  return api.get<SurveyLinkableTypeDto[]>('/surveys/linkable-types')
}

/**
 * The picker, narrowed BY THE POLL.
 *
 * Candidates come back filtered by seat type and by the poll's geographic
 * branch, in `politics` — the only feature that knows a seat type implies a
 * unit level. PollSphere listed every candidate in the deployment and left the
 * combination to a validator that did not exist.
 */
export function suggestSurveyLinks(surveyId: string, params: {
  typeCode: string
  q?: string
  filterCode?: string
  limit?: number
}) {
  return api.get<SurveyLinkSuggestionDto[]>(
    `/surveys/${surveyId}/link-suggestions${qs(params as Record<string, QueryValue>)}`,
  )
}

/**
 * The sample-size verdict for a poll that exists.
 *
 * The three overrides are what make the wizard's fields checkable BEFORE they
 * are saved — without them a person writes a target, saves it, reads the
 * verdict and writes it again.
 */
export function getSampleSize(surveyId: string, overrides: {
  marginOfError?: number | string
  confidenceLevel?: number
  targetSample?: number
} = {}) {
  return api.get<SurveySampleSizeDto>(
    `/surveys/${surveyId}/sample-size${qs(overrides as Record<string, QueryValue>)}`,
  )
}

/** The same calculator before the poll exists — the wizard's first step. */
export function calculateSampleSize(params: {
  marginOfError: number | string
  confidenceLevel?: number
  targetSample?: number
  populationN?: number
}) {
  return api.get<SurveySampleSizeDto>(
    `/surveys/sample-size${qs(params as Record<string, QueryValue>)}`,
  )
}

export function getSchedule(surveyId: string) {
  return api.get<SurveyScheduleDto>(`/surveys/${surveyId}/schedule`)
}

/**
 * Set the recurrence.
 *
 * `autoWave` on a poll outside a tracker, or on a frequency with no interval,
 * is a 409 with a sentence — never an accepted setting that quietly never
 * fires. `autoWaveBlocked` on the read lets the screen say so first.
 */
export function setSchedule(surveyId: string, body: {
  frequencyCode?: string
  autoWave: boolean
  firstRunAt?: string | null
}) {
  return api.put<SurveyScheduleDto>(`/surveys/${surveyId}/schedule`, body)
}

/** Open the tracker's next wave now — the same path the scheduler takes. */
export function openNextWave(surveyId: string) {
  return api.post<SurveyScheduleDto>(`/surveys/${surveyId}/schedule/open-next-wave`, {})
}

/**
 * Identify a respondent by the national ID they handed over (backend prompt 64).
 *
 * POST, with the ID in the BODY — a national ID in a query string is a national
 * ID in the access log, the browser history and every proxy in between.
 * `survey:field`, not `voter:read`: the interviewer needs one record for the
 * person in front of them, not the register.
 */
export function lookUpRespondent(surveyId: string, nationalId: string) {
  return api.post<RespondentLookupDto>(`/surveys/${surveyId}/respondent-lookup`, { nationalId })
}

// ── the estimator ────────────────────────────────────────────────────────────

/**
 * The Estimator console's payload — `GET /surveys/{id}/estimator`.
 *
 * ## Declared here rather than in `shared/api/types`
 *
 * These shapes are read by exactly one screen. Everything in the shared types file is read by
 * several, and the file is long enough that a type used once is a type somebody has to rule
 * out when they go looking.
 *
 * ## Two rules of this module are in the shapes themselves
 *
 * **`estimate` is null when the cell is suppressed**, and `suppressedSentence` is what a
 * client renders in its place — never a zero, never a dash. **Every rate is null rather than
 * zero when its denominator is empty**: "0% response rate" and "we have not started" are
 * different statements, and only one of them is a fieldwork problem.
 */
export interface EstimatorPollRef {
  id: string
  ref: string
  title: string
  statusCode: string
  methodologyCode: string
  scopeUnitName: string | null
  targetSample: number
  collectedCount: number
  waveNo: number | null
  groupName: string | null
}

export interface EstimatorQuestionRef {
  id: string
  code: string
  text: string
  optionCount: number
}

export interface EstimatorDesign {
  weightRunId: string | null
  methodCode: string | null
  converged: boolean | null
  baseN: number
  effectiveN: number | null
  deff: number | null
  maxDeviation: number | null
  trimAt: number | null
  trimmedCount: number
  computedAt: string | null
}

/** One option, as a distribution: `estimate` and `standardError` are all a curve needs. */
export interface EstimatorBand {
  optionId: string
  optionCode: string
  optionLabel: string
  rank: number
  estimate: number | null
  standardError: number | null
  moe95: number | null
  ciLower: number | null
  ciUpper: number | null
  baseN: number
  effectiveN: number | null
  suppressed: boolean
  suppressedSentence: string | null
}

/**
 * The gap between two options, with its own margin.
 *
 * `gapStandardError` is NOT √(se₁² + se₂²): the two shares come from one multinomial and are
 * negatively correlated, so the server uses `[p₁ + p₂ − (p₁ − p₂)²] / n_eff`. `separated` is
 * the honest test; `intervalsOverlap` is what a reader concludes by eye from a chart with
 * error bars, and the two disagree more often than anybody expects.
 */
export interface EstimatorSeparation {
  aOptionId: string
  aLabel: string
  bOptionId: string
  bLabel: string
  gap: number
  gapStandardError: number | null
  gapMoe95: number | null
  leadProbability: number | null
  separated: boolean | null
  intervalsOverlap: boolean | null
}

export interface EstimatorBudget {
  atOptionLabel: string
  atEstimate: number
  simpleRandomMoe: number
  realisedMoe: number
  inflation: number | null
  baseN: number
  effectiveN: number | null
  lostInterviews: number
}

export interface EstimatorAttrition {
  targetSample: number
  eligibleBase: number
  reachedBase: number
  complete: number
  partial: number
  refusal: number
  nonContact: number
  otherNonResponse: number
  ineligible: number
  analysed: number
  flagged: number
  responseRate1: number | null
  responseRate3: number | null
  cooperationRate1: number | null
  refusalRate1: number | null
  contactRate1: number | null
  suppressedReason: string | null
}

export interface EstimatorWeightCell {
  dimensionCode: string
  valueCode: string
  targetShare: number | null
  achievedShare: number | null
  deviation: number | null
}

export interface EstimatorSubgroup {
  dimensionCode: string
  valueCode: string
  baseN: number
  effectiveN: number | null
  worstMoe: number | null
  thin: boolean
  suppressedCells: number
}

export interface Estimator {
  generatedAt: string
  poll: EstimatorPollRef
  question: EstimatorQuestionRef | null
  questions: EstimatorQuestionRef[]
  design: EstimatorDesign | null
  bands: EstimatorBand[]
  separations: EstimatorSeparation[]
  budget: EstimatorBudget | null
  attrition: EstimatorAttrition | null
  weighting: EstimatorWeightCell[]
  subgroups: EstimatorSubgroup[]
  /** Why there is nothing to draw, when there is nothing. Never an empty payload. */
  unavailable: string | null
}

export const getEstimator = (surveyId: string, questionId?: string) =>
  api.get<Estimator>(`/surveys/${surveyId}/estimator${questionId ? `?questionId=${questionId}` : ''}`)
